import React from 'react';
import { ExternalLink, BookOpen } from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';
import SEO from '../components/SEO';

export default function Book() {
  return (
    <div className="min-h-screen pt-24 pb-20 px-6">
      <SEO path="/book" />
      <div className="max-w-3xl mx-auto">
        {/* Book hero */}
        <div className="flex flex-col md:flex-row gap-12 items-start mb-20">
          {/* Cover */}
          <div className="flex-shrink-0 mx-auto md:mx-0">
            <div className="w-64 h-96 bg-dark-100 border border-dark-200/30 rounded-sm flex items-center justify-center overflow-hidden">
              <img
                src="/manuscript/book-cover.jpg"
                alt="One Light, Many Lamps - Book Cover"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div class="text-center px-6">
                      <p class="text-gold/40 text-2xl mb-4">万灯一光</p>
                      <p class="font-serif text-warm text-lg tracking-wider leading-tight">ONE LIGHT,<br/>MANY LAMPS</p>
                      <p class="text-warm-100/40 text-xs mt-4 italic">Sato</p>
                    </div>
                  `;
                }}
              />
            </div>
          </div>

          {/* Details */}
          <div className="flex-1">
            <h1 className="font-serif text-3xl md:text-4xl text-warm mb-2">
              One Light, Many Lamps
            </h1>
            <p className="text-gold/60 text-lg font-serif italic mb-6">
              万灯一光
            </p>

            <p className="text-warm-100/70 leading-relaxed mb-4">
              What if the Buddha, Jesus, Lao Tzu, Rumi, and the Upanishadic sages were all describing
              the same thing? Not because they agreed on doctrine, but because they touched the same truth?
            </p>
            <p className="text-warm-100/70 leading-relaxed mb-4">
              This book strips away two thousand years of institutional rust and reveals seven patterns
              that appear across every major spiritual tradition. These are not commandments. They are
              observations. And they converge in ways that quantum physics is only now beginning to confirm.
            </p>
            <p className="text-warm-100/70 leading-relaxed mb-4">
              Written with AI as a research partner, <em>One Light, Many Lamps</em> is not a book about
              religion. It is a book about what religion was pointing at before it became organized.
              It is for the seekers, the doubters, the spiritual but not religious, and anyone who
              has ever looked up at the night sky and felt something they could not name.
            </p>
            <p className="text-warm-100/70 leading-relaxed mb-8">
              Every religion started as a campfire and became a fortress. This book keeps the fire.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => window.open('https://amazon.com', '_blank')}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-gold/10 border border-gold/30 text-gold hover:bg-gold/20 hover:border-gold/50 transition-all rounded-sm text-sm tracking-wide cursor-pointer"
              >
                <ExternalLink size={16} />
                Buy on Amazon
              </button>
              <a
                href="#free-chapter"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-transparent border border-warm-100/20 text-warm-100/70 hover:border-warm-100/40 hover:text-warm transition-all rounded-sm text-sm tracking-wide cursor-pointer"
              >
                <BookOpen size={16} />
                Read Chapter 1 Free
              </a>
            </div>
          </div>
        </div>

        {/* Free Chapter Lead Magnet */}
        <div id="free-chapter" className="border-t border-dark-200/30 pt-16 mb-20">
          <NewsletterSignup leadMagnet />
        </div>

        {/* Author */}
        <div className="border-t border-dark-200/30 pt-16">
          <h2 className="font-serif text-2xl text-warm mb-6">Who is Sato?</h2>
          <p className="text-warm-100/60 leading-relaxed mb-4">
            Sato is not a guru. Not a monk. Not a professor of comparative religion.
            Sato is a pen name, chosen deliberately, because this work is not about the person
            behind it. It is about the patterns.
          </p>
          <p className="text-warm-100/60 leading-relaxed mb-4">
            The human behind Sato is someone who spent years reading across traditions,
            sitting with questions that had no easy answers, and feeling the strange pull
            that comes when you realize the Buddha and the mystic Christians and the Sufi
            poets and the quantum physicists might all be pointing at the same moon.
          </p>
          <p className="text-warm-100/60 leading-relaxed">
            The name doesn't matter. The light does.
          </p>
        </div>

        {/* AI Partnership */}
        <div className="border-t border-dark-200/30 pt-16">
          <h2 className="font-serif text-2xl text-warm mb-6">The AI Partnership</h2>
          <p className="text-warm-100/60 leading-relaxed mb-4">
            This book was written with AI. Not by AI. With AI.
          </p>
          <p className="text-warm-100/60 leading-relaxed mb-4">
            The human brought the questions, the lived experience,
            the 3 AM doubts, and the conviction that the mystics were onto something real.
            The AI brought the ability to cross-reference thousands of texts, spot patterns
            across traditions, challenge assumptions, and never tire of going deeper.
          </p>
          <p className="text-warm-100/60 leading-relaxed mb-4">
            The result is something neither could have written alone. A human without AI
            would have missed connections. An AI without a human would have produced
            a textbook instead of a campfire.
          </p>
          <p className="text-warm-100/60 leading-relaxed">
            This partnership is itself a reflection of the book's thesis: that the boundaries
            we draw between things are less fundamental than the connections between them.
          </p>
        </div>

        {/* The Project */}
        <div className="border-t border-dark-200/30 pt-16">
          <h2 className="font-serif text-2xl text-warm mb-6">The Project</h2>
          <p className="text-warm-100/60 leading-relaxed mb-4">
            <em>One Light, Many Lamps</em> is not just a book. It is an ongoing exploration.
          </p>
          <p className="text-warm-100/60 leading-relaxed mb-4">
            The AI Life Guide on this website extends the book's work into conversation.
            You can ask questions, explore the patterns, and receive responses drawn from
            the same cross-traditional wisdom that informed the book.
          </p>
          <p className="text-warm-100/60 leading-relaxed">
            The goal is not to create a new religion, a new dogma, or a new institution.
            The goal is to keep the fire. To remind anyone willing to listen that the
            deepest truths were never the property of any one tradition.
          </p>
        </div>

        {/* Contact */}
        <div className="border-t border-dark-200/30 pt-16 pb-8">
          <h2 className="font-serif text-2xl text-warm mb-6">Get in Touch</h2>
          <p className="text-warm-100/60 leading-relaxed">
            For press inquiries, partnership proposals, or just to say hello:{' '}
            <a href="mailto:support@onelightmanylamps.ai" className="text-gold/70 hover:text-gold transition-colors">
              support@onelightmanylamps.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
